import { useCallback, useEffect, useMemo, useState } from "react"
import { Pressable, ScrollView, Text, TextInput, View } from "react-native"
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import dayjs from 'dayjs'
import { Dialog } from "../../components"
import { axiosInstance } from "../../api/Middleware"
import { DotationEsag } from "../../api/Dotation/types"
import { useSessionStore } from "../../lib/Store/sessionStore.component"
import { CET } from "../GestionCET/GestionCET.types"
import useEspaceValideurStyle, { couleursValideur } from "./EspaceValideur.styles"
import { CompteurDemande, Decision, SoldeDemande, ValideurDetailDemandeProps } from "./EspaceValideur.types"
import { estAbsence, estTerminee, formatHeures, formatJours, formatNombre, heuresTexteEnMinutes, joursDemandeCet, texteDuree, textePeriode, toNumber } from "./EspaceValideur.utils"

const ICONES_NATURE: Record<string, string> = {
    CP: 'palm-tree',
    RTT: 'calendar-check',
}

export default function ValideurDetailDemande({demande, etat, choixInitial, joursCetEnAttente, onConfirm, onClose, onOpenPlanning}: ValideurDetailDemandeProps){
    const { styles, isResponsive } = useEspaceValideurStyle();
    const { getCurrentInfos } = useSessionStore();
    const { currentEntite } = getCurrentInfos();
    const heuresParJour = currentEntite?.collectivitegr?.dureeJourEnHeureCet || 7;
    const plafondCet = currentEntite?.collectivitegr?.limiteCet || 60;

    // La réponse du valideur reste modifiable tant que la chaîne de validation n'est pas terminée
    const lectureSeule = estTerminee(demande);
    const dejaTraitee = etat !== 'attente';
    const absence = estAbsence(demande);
    const libelleNature = demande.natureAbsence?.libelle || '';
    const enHeures = absence && demande.duree?.profil !== 1;

    // La décision déjà prise ne peut pas être renvoyée : seule la décision inverse reste accessible
    const [choix, setChoix] = useState<Decision | undefined>(choixInitial !== etat ? choixInitial : undefined);
    const [commentaire, setCommentaire] = useState('');
    const [detailOuvert, setDetailOuvert] = useState(false);
    const [envoiEnCours, setEnvoiEnCours] = useState(false);
    const [dotation, setDotation] = useState<DotationEsag>();
    const [soldeCet, setSoldeCet] = useState<CET>();

    const commentaireValide = choix !== 'refuse' || commentaire.trim().length > 0;
    const bloque = lectureSeule || !choix || choix === etat || !commentaireValide || envoiEnCours;
    // Tant qu'aucune nouvelle décision n'est choisie, le bouton de confirmation rappelle la réponse enregistrée
    const sansDecisionAEnvoyer = lectureSeule || (dejaTraitee && !choix);

    useEffect(() => {
        if(estAbsence(demande)){
            const typeAPI = demande.natureAbsence?.code === 'CP' ? 'sconges' : 'squotas';
            axiosInstance.get<DotationEsag>(
                `/personnel/personnel/${typeAPI}/dotationEsag?identifiantAgent=${demande.identifiantAgent}&identifiantNatureAbsence=${demande.identifiantNatureAbsence}&dateAcquisition=${dayjs().startOf('year').format('YYYYMMDD')}`
            ).then((response) => setDotation(response.data))
        } else {
            axiosInstance.get<CET>(`/personnel/personnel/cets/dotation-agent?identifiantEntite=${currentEntite?.identifiantEntite}&identifiantAgent=${demande.identifiantAgent}`)
                .then((response) => setSoldeCet(response.data))
        }
    }, [currentEntite?.identifiantEntite, demande])

    // Effet de la demande sur le solde de l'agent (aucun effet en cas de refus)
    const solde = useMemo<SoldeDemande | null>(() => {
        const refus = choix === 'refuse';
        if(absence){
            if(!dotation){
                return null;
            }
            const libelle = `Solde ${libelleNature.toLowerCase()}`;
            const delta = refus ? 'aucun effet' : `− ${texteDuree(demande, heuresParJour)}`;
            if(enHeures){
                const avant = heuresTexteEnMinutes(dotation.reste);
                const apres = refus ? avant : avant - Math.round((demande.duree?.dureeHeures || 0) * 60);
                return { libelle, avant: formatHeures(avant), apres: formatHeures(apres), delta };
            }
            const avant = toNumber(dotation.reste);
            const apres = refus ? avant : avant - (demande.duree?.dureeJours || 0);
            return { libelle, avant: formatJours(avant), apres: formatJours(apres), delta };
        }
        if(!soldeCet){
            return null;
        }
        const avant = toNumber(soldeCet.reste);
        const apres = refus ? avant : avant + joursDemandeCet(demande, heuresParJour);
        return {
            libelle: 'Solde du compte épargne temps',
            avant: formatJours(avant),
            apres: formatJours(apres),
            delta: refus ? 'aucun effet' : `+ ${texteDuree(demande, heuresParJour)}`,
        };
    }, [absence, choix, demande, dotation, enHeures, heuresParJour, libelleNature, soldeCet])

    const compteur = useMemo<CompteurDemande | null>(() => {
        const aujourdhui = dayjs().format('DD/MM/YYYY');
        if(absence && dotation){
            return {
                titre: libelleNature,
                icone: ICONES_NATURE[demande.natureAbsence?.code || ''] || 'clock-outline',
                couleur: demande.natureAbsence?.couleurHexa || couleursValideur.bleu,
                reste: `${dotation.reste} ${enHeures ? 'heures' : 'jours'}`,
                colonnes: [
                    { libelle: 'Droit', valeur: dotation.droit },
                    { libelle: 'Pris', valeur: dotation.pris },
                    { libelle: 'Solde actuel', valeur: dotation.solde },
                    { libelle: 'À venir', valeur: dotation.pris_a_venir },
                    { libelle: 'À valider', valeur: dotation.pris_a_valider },
                ],
                pied: `Compteur au ${aujourdhui} · période de référence ${dayjs().year()}`,
            };
        }
        if(!absence && soldeCet){
            const epargnes = toNumber(soldeCet.epargnes);
            return {
                titre: 'Compte épargne temps',
                icone: 'piggy-bank-outline',
                couleur: couleursValideur.metierSecondaire,
                reste: `${formatNombre(toNumber(soldeCet.reste))} jours`,
                colonnes: [
                    { libelle: 'Plafond', valeur: formatNombre(plafondCet) },
                    { libelle: 'Épargnés', valeur: formatNombre(epargnes) },
                    { libelle: 'À valider', valeur: formatNombre(joursCetEnAttente) },
                    { libelle: 'Marge', valeur: formatNombre(Math.max(0, plafondCet - epargnes - joursCetEnAttente)) },
                ],
                pied: `Compteur au ${aujourdhui} · base 1 jour = ${heuresParJour} heures`,
            };
        }
        return null;
    }, [absence, demande.natureAbsence, dotation, enHeures, heuresParJour, joursCetEnAttente, libelleNature, plafondCet, soldeCet])

    const infos = useMemo(() => [
        { libelle: "Nature d'absence", valeur: libelleNature },
        absence
            ? { libelle: 'Période demandée', valeur: textePeriode(demande) }
            : { libelle: 'Quantité à épargner', valeur: texteDuree(demande, heuresParJour) },
        { libelle: 'Date de création', valeur: dayjs(demande.dateDemande).format('DD/MM/YYYY') },
        ...(absence ? [{ libelle: 'Durée', valeur: texteDuree(demande, heuresParJour) }] : []),
        ...(absence && demande.observations ? [{ libelle: "Commentaire de l'agent", valeur: demande.observations }] : []),
    ], [absence, demande, heuresParJour, libelleNature])

    const toggleDetail = useCallback(() => setDetailOuvert((old) => !old), [])
    const choisirValidation = useCallback(() => setChoix('valide'), [])
    const choisirRefus = useCallback(() => setChoix('refuse'), [])

    const handleConfirm = useCallback(() => {
        if(bloque || !choix){
            return;
        }
        setEnvoiEnCours(true);
        onConfirm(choix, commentaire.trim()).finally(() => setEnvoiEnCours(false));
    }, [bloque, choix, commentaire, onConfirm])

    const surValidation = choix === 'valide';
    const surRefus = choix === 'refuse';
    const couleurFleche = surRefus ? couleursValideur.grey600 : absence ? couleursValideur.roseFg : couleursValideur.vertFg;
    const fondFleche = surRefus ? couleursValideur.grey100 : absence ? couleursValideur.roseBg : couleursValideur.vertBg;

    let libelleConfirmer = 'Choisissez une décision';
    if(sansDecisionAEnvoyer){
        libelleConfirmer = etat === 'valide' ? 'Demande validée' : 'Demande refusée';
    } else if(surRefus){
        libelleConfirmer = 'Confirmer le refus';
    } else if(surValidation){
        libelleConfirmer = 'Confirmer la validation';
    }
    let fondConfirmer: string = couleursValideur.primaire;
    if(sansDecisionAEnvoyer){
        fondConfirmer = couleursValideur.grey100;
    } else if(bloque){
        fondConfirmer = couleursValideur.primaireDesactive;
    } else if(surRefus){
        fondConfirmer = couleursValideur.danger;
    }

    return(
        <Dialog
            visible
            fitContent
            width={isResponsive ? '100%' : 460}
            title={`Demande ${absence ? "d'absence" : 'de CET'} de ${demande.agent?.nomClassement || ''}`}
            onClose={onClose}
        >
            <ScrollView style={styles.defilement} contentContainerStyle={styles.decision}>
                <Text style={styles.decisionSousTitre}>{absence ? libelleNature : `Épargne · ${libelleNature}`}</Text>

                <Pressable onPress={onOpenPlanning} style={styles.lienPlanning}>
                    <View style={styles.lienPlanningIcone}>
                        <MaterialCommunityIcons name="calendar-range" size={16} color={couleursValideur.bleu}/>
                    </View>
                    <Text style={styles.lienPlanningTexte}>Planning du jour</Text>
                    <MaterialCommunityIcons name="chevron-right" size={16} color={couleursValideur.grey450}/>
                </Pressable>

                <View style={styles.infos}>
                    {infos.map((info) => (
                        <View key={info.libelle} style={styles.info}>
                            <Text style={styles.infoLabel}>{info.libelle}</Text>
                            <Text style={styles.infoValeur}>{info.valeur}</Text>
                        </View>
                    ))}
                </View>

                <View style={styles.bloc}>
                    <View style={styles.soldeEnTete}>
                        <Text style={styles.soldeLabel}>{solde?.libelle || 'Solde'}</Text>
                        {compteur && (
                            <Pressable
                                onPress={toggleDetail}
                                style={[styles.boutonDetail, {
                                    borderColor: detailOuvert ? couleursValideur.bleu : couleursValideur.grey300,
                                    backgroundColor: detailOuvert ? couleursValideur.bleuClair : couleursValideur.blanc,
                                }]}
                            >
                                <MaterialCommunityIcons name="file-tree-outline" size={15} color={detailOuvert ? couleursValideur.bleu : couleursValideur.grey600}/>
                            </Pressable>
                        )}
                    </View>
                    {solde ? (
                        <View style={styles.soldeLigne}>
                            <View style={styles.soldeElement}>
                                <Text style={styles.soldeElementLabel}>Avant</Text>
                                <Text style={styles.soldeValeur}>{solde.avant}</Text>
                            </View>
                            <View style={styles.soldeFleche}>
                                <View style={[styles.soldeFlecheIcone, { backgroundColor: fondFleche }]}>
                                    <MaterialCommunityIcons name="arrow-right" size={16} color={couleurFleche}/>
                                </View>
                                <Text style={[styles.soldeDelta, { color: couleurFleche }]}>{solde.delta}</Text>
                            </View>
                            <View style={styles.soldeElement}>
                                <Text style={styles.soldeElementLabel}>Après</Text>
                                <Text style={[styles.soldeValeur, !absence && !surRefus && { color: couleursValideur.succes }]}>{solde.apres}</Text>
                            </View>
                        </View>
                    ) : (
                        <Text style={styles.soldeChargement}>Chargement du solde…</Text>
                    )}
                    {detailOuvert && compteur && (
                        <View style={styles.detail}>
                            <View style={styles.detailTitre}>
                                <MaterialCommunityIcons name={compteur.icone} size={17} color={compteur.couleur}/>
                                <Text style={[styles.detailTitreTexte, { color: compteur.couleur }]}>{compteur.titre}</Text>
                            </View>
                            <View style={styles.detailReste}>
                                <Text style={styles.detailResteLabel}>Il reste à {demande.agent?.nomClassement}</Text>
                                <Text style={[styles.detailResteValeur, { color: compteur.couleur }]}>{compteur.reste}</Text>
                            </View>
                            <View style={styles.detailColonnes}>
                                {compteur.colonnes.map((colonne) => (
                                    <View key={colonne.libelle} style={styles.detailColonne}>
                                        <Text style={styles.detailColonneLabel}>{colonne.libelle}</Text>
                                        <Text style={styles.detailColonneValeur}>{colonne.valeur}</Text>
                                    </View>
                                ))}
                            </View>
                            <Text style={styles.detailPied}>{compteur.pied}</Text>
                        </View>
                    )}
                </View>

                <View style={styles.champ}>
                    <Text style={styles.champLabel}>
                        Commentaire {surRefus && <Text style={styles.obligatoire}>*</Text>}
                    </Text>
                    <TextInput
                        style={[
                            styles.commentaire,
                            !commentaireValide && styles.commentaireErreur,
                            lectureSeule && styles.commentaireLectureSeule,
                        ]}
                        multiline
                        numberOfLines={4}
                        editable={!lectureSeule}
                        placeholder={surRefus ? "Motif du refus, transmis à l'agent" : 'Ajouter un commentaire (facultatif)'}
                        placeholderTextColor={couleursValideur.grey450}
                        value={commentaire}
                        onChangeText={setCommentaire}
                    />
                    {!lectureSeule && !commentaireValide && (
                        <Text style={styles.messageErreur}>Le motif est obligatoire pour un refus.</Text>
                    )}
                </View>

                <View style={styles.choix}>
                    <Pressable
                        disabled={lectureSeule || etat === 'valide'}
                        onPress={choisirValidation}
                        style={[
                            styles.boutonChoix,
                            surValidation && { borderColor: couleursValideur.vertFg, backgroundColor: couleursValideur.vertBg },
                            etat === 'valide' && styles.boutonChoixDesactive,
                        ]}
                    >
                        <MaterialCommunityIcons name="check" size={16} color={surValidation ? couleursValideur.vertFg : couleursValideur.grey800}/>
                        <Text style={[styles.boutonChoixTexte, surValidation && { color: couleursValideur.vertFg }]}>Valider</Text>
                    </Pressable>
                    <Pressable
                        disabled={lectureSeule || etat === 'refuse'}
                        onPress={choisirRefus}
                        style={[
                            styles.boutonChoix,
                            surRefus && { borderColor: couleursValideur.danger, backgroundColor: couleursValideur.roseBg },
                            etat === 'refuse' && styles.boutonChoixDesactive,
                        ]}
                    >
                        <MaterialCommunityIcons name="close" size={16} color={surRefus ? couleursValideur.danger : couleursValideur.grey800}/>
                        <Text style={[styles.boutonChoixTexte, surRefus && { color: couleursValideur.danger }]}>Refuser</Text>
                    </Pressable>
                </View>

                <Pressable disabled={bloque} onPress={handleConfirm} style={[styles.boutonConfirmer, { backgroundColor: fondConfirmer }]}>
                    <Text style={[styles.boutonConfirmerTexte, { color: sansDecisionAEnvoyer ? couleursValideur.grey600 : couleursValideur.blanc }]}>
                        {libelleConfirmer}
                    </Text>
                </Pressable>
            </ScrollView>
        </Dialog>
    )
}
